// Best-match ranking for the store and camera pickers.
//
// A VMS accumulates stores and cameras faster than anyone can remember their
// exact names, so requiring a substring match ("downtown" finding nothing
// because the store is called "Down-Town #4") is the wrong behaviour. This
// ranks candidates instead: exact, then prefix, then word-start, then
// subsequence, with ties broken by how tightly the match sits in the text.
//
// Deliberately plain and synchronous: these lists are hundreds of items at
// most, so a real index would cost more than it saves and would need
// invalidating on every camera add.

export type Ranked<T> = { item: T; score: number };

const EXACT = 1000;
const PREFIX = 800;
const WORD_START = 600;
const SUBSTRING = 400;
const SUBSEQUENCE = 200;

function normalize(value: string): string {
  return value.toLowerCase().trim();
}

/** Are all of `query`'s characters present in `text`, in order? */
function subsequenceSpan(text: string, query: string): number | null {
  let ti = 0;
  let start = -1;
  for (const ch of query) {
    const found = text.indexOf(ch, ti);
    if (found === -1) return null;
    if (start === -1) start = found;
    ti = found + 1;
  }
  return ti - start;
}

/** Score one field, 0 when it does not match at all. */
export function scoreField(field: string, query: string): number {
  const text = normalize(field);
  const q = normalize(query);
  if (!q) return 1;
  if (!text) return 0;

  if (text === q) return EXACT;
  if (text.startsWith(q)) return PREFIX - Math.min(text.length - q.length, 99);

  // Word starts: "ent cam" should find "Entrance Camera".
  const words = text.split(/[\s\-_/.]+/).filter(Boolean);
  if (words.some((w) => w.startsWith(q))) {
    return WORD_START - Math.min(text.length - q.length, 99);
  }
  const queryWords = q.split(/\s+/).filter(Boolean);
  if (
    queryWords.length > 1 &&
    queryWords.every((qw) => words.some((w) => w.startsWith(qw)))
  ) {
    return WORD_START - 50;
  }

  const at = text.indexOf(q);
  if (at !== -1) return SUBSTRING - Math.min(at, 99);

  const span = subsequenceSpan(text, q);
  if (span !== null) {
    // A tight span means the characters were close together, which reads as a
    // better match than the same letters scattered across a long name.
    return SUBSEQUENCE - Math.min(span - q.length, 199);
  }
  return 0;
}

/**
 * Rank items by their best-scoring field.
 *
 * `fields` is ordered by importance: earlier fields win ties, so a hit on a
 * store's name outranks the same hit on its location.
 */
export function rankItems<T>(
  items: readonly T[],
  query: string,
  fields: (item: T) => Array<string | undefined | null>,
): Ranked<T>[] {
  const q = query.trim();
  const out: Ranked<T>[] = [];
  for (const item of items) {
    if (!q) {
      out.push({ item, score: 1 });
      continue;
    }
    let best = 0;
    const values = fields(item);
    values.forEach((value, index) => {
      if (!value) return;
      const score = scoreField(value, q);
      // Later fields are worth slightly less, so a name match beats an id
      // match of the same shape.
      if (score > 0) best = Math.max(best, score - index);
    });
    if (best > 0) out.push({ item, score: best });
  }
  if (q) out.sort((a, b) => b.score - a.score);
  return out;
}

/** Convenience: ranked items only, original order preserved for an empty query. */
export function searchItems<T>(
  items: readonly T[],
  query: string,
  fields: (item: T) => Array<string | undefined | null>,
): T[] {
  return rankItems(items, query, fields).map((r) => r.item);
}
